import React, { Component, ErrorInfo, ReactNode } from 'react';
import { toast } from 'sonner';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Don't reload when we have a custom fallback (e.g. RunningActive load failure)
    if (this.props.fallback) return;
    
    // Show user-friendly toast for rate limit errors
    const errorMsg = error?.message || '';
    if (errorMsg.includes('429') || errorMsg.includes('daily_limit')) {
      toast.error("You've reached today's limit of 30 AI messages. Your limit will reset tomorrow.");
    } else {
      toast.error('An unexpected error occurred. The page will reload.');
      // Auto-reload after a brief delay for non-rate-limit errors
      setTimeout(() => window.location.reload(), 2000);
    }
  }

  public render() {
    if (this.state.hasError) {
      // Return fallback UI or children (app continues working)
      return this.props.fallback || this.props.children;
    }

    return this.props.children;
  }
}
